import { AUTH_STRATEGY, decodeToken, Loading } from "@dashboard/library";
import Cookies from "js-cookie";
import React, {
  createContext,
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  COOKIE_KEYS,
  REFRESH_TOKEN_INTERVAL,
} from "@/constants/auth.constants";
import {
  getAuthRefreshToken,
  getAuthToken,
  removeAuthCookies,
  requestRefreshToken,
  setAuthCookies,
} from "@/helpers/authentication/authentication.helpers";
import { redirectToDashboard } from "@/helpers/navigation/navigation.helpers";
import {
  AUTH_STATES,
  AUTHENTICATION_STATUS,
  AuthenticationActionType,
  AuthenticationContext,
  AuthenticationState,
} from "./AuthenticationProvider.decl";

const authenticationState: AuthenticationState = {
  status: AUTHENTICATION_STATUS.UNKNOWN,
  signedInError: null,
  signedInStrategy: null,
  isConnectedAs: false,
  token: null,
  decodedToken: undefined,
};

function authenticationReducer(
  state: AuthenticationState,
  action: AuthenticationActionType
): AuthenticationState {
  switch (action.type) {
    case AUTH_STATES.SIGN_IN_SUCCESS: {
      const { isConnectedAs, token, decodedToken, strategy } = action.payload;

      return {
        ...state,
        signedInStrategy: strategy,
        status: AUTHENTICATION_STATUS.AUTHENTICATED,
        isConnectedAs,
        token,
        decodedToken,
      };
    }
    case AUTH_STATES.SIGN_IN_ERROR:
      return {
        ...state,
        status: AUTHENTICATION_STATUS.NOT_AUTHENTICATED,
        signedInError: action.payload.message,
        signedInStrategy: action.payload.strategy,
      };
    case AUTH_STATES.REFRESH_TOKEN:
      return {
        ...state,
        token: action.payload.token,
      };
    case AUTH_STATES.SIGN_OUT:
      return {
        ...authenticationState,
        status: AUTHENTICATION_STATUS.NOT_AUTHENTICATED,
      };
    /* istanbul ignore next */
    default:
      throw new Error("Unexpected authentication action type");
  }
}

export const AuthenticationStateContext = createContext<AuthenticationContext>(
  {} as AuthenticationContext
);

export function AuthenticationProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [authState, dispatch] = useReducer(
    authenticationReducer,
    authenticationState
  );

  const setSignInError = useCallback(
    (message: string | null, strategy = AUTH_STRATEGY.PASSWORD) => {
      dispatch({
        type: AUTH_STATES.SIGN_IN_ERROR,
        payload: { message, strategy },
      });
    },
    []
  );

  const checkAuth = useCallback(async () => {
    const token = getAuthToken();
    const isConnectedAs =
      Cookies.get(COOKIE_KEYS.IS_CONNECTED_AS) === "true" ||
      !!Cookies.get(COOKIE_KEYS.IMPERSONATE_TOKEN);

    if (token) {
      const strategy = Cookies.get(COOKIE_KEYS.AUTH_STRATEGY) as AUTH_STRATEGY;
      const decodedToken = decodeToken(token);

      return dispatch({
        type: AUTH_STATES.SIGN_IN_SUCCESS,
        payload: {
          token,
          isConnectedAs,
          decodedToken,
          strategy,
        },
      });
    }

    return dispatch({
      type: AUTH_STATES.SIGN_OUT,
    });
  }, []);

  const refreshToken = useCallback(async () => {
    const token = getAuthToken();
    const refreshToken = getAuthRefreshToken();

    if (!token || !refreshToken) {
      throw new Error("No token or refresh token.");
    }

    const { idToken: newToken, refreshToken: newRefreshToken } =
      await requestRefreshToken({
        token,
        refreshToken,
      });

    setAuthCookies(newToken, newRefreshToken);

    return dispatch({
      type: AUTH_STATES.REFRESH_TOKEN,
      payload: { token: newToken },
    });
  }, []);

  const onSessionExpired = useCallback(() => {
    removeAuthCookies();
    // It should be dashboard responsability to logout user
    redirectToDashboard(`/login?redirect=${window.location.href}`);
  }, []);

  const value: AuthenticationContext = useMemo(
    () => ({
      authState,
      actions: {
        onSessionExpired,
        refreshToken,
        setSignInError,
      },
    }),
    [authState, onSessionExpired, refreshToken, setSignInError]
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authState.status === AUTHENTICATION_STATUS.AUTHENTICATED) {
      const intervalId = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          onSessionExpired();
        }
      }, REFRESH_TOKEN_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [authState.status, refreshToken, onSessionExpired]);

  // We need this loading otherwise this provider re-renders
  // when loaded is true and refetch global state
  if (authState.status === AUTHENTICATION_STATUS.UNKNOWN) {
    return <Loading data-test="auth-loading" />;
  }

  return (
    <AuthenticationStateContext.Provider value={value}>
      {children}
    </AuthenticationStateContext.Provider>
  );
}
